import { clampZoom } from './configurePositions.action'

export interface SelectionRect {
  left: number
  top: number
  right: number
  bottom: number
}

export interface Box2D {
  x: number
  y: number
  width: number
  height: number
}

export interface CenterPoint {
  id: number
  x: number
  y: number
}

export interface ZoomAnchor {
  ratioX: number
  ratioY: number
  offsetX: number
  offsetY: number
}

const WHEEL_ZOOM_SENSITIVITY = 0.0025
const WHEEL_LINE_HEIGHT_PX = 16
const TOOLTIP_CURSOR_GAP_X = 14
const TOOLTIP_CURSOR_GAP_Y = 16
const TOOLTIP_FLIP_GAP = 12
const TOOLTIP_EDGE_PADDING = 4
const CAGE_PROXIMITY_TOLERANCE = 1.2

export const toSelectionRect = (startX: number, startY: number, endX: number, endY: number): SelectionRect => {
  return {
    left: Math.min(startX, endX),
    top: Math.min(startY, endY),
    right: Math.max(startX, endX),
    bottom: Math.max(startY, endY),
  }
}

const pointInRect = (x: number, y: number, rect: SelectionRect) => {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
}

const crossProduct = (x1: number, y1: number, x2: number, y2: number) => {
  return x1 * y2 - y1 * x2
}

const segmentsCross = (
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
) => {
  const d1 = crossProduct(bx - ax, by - ay, cx - ax, cy - ay)
  const d2 = crossProduct(bx - ax, by - ay, dx - ax, dy - ay)
  const d3 = crossProduct(dx - cx, dy - cy, ax - cx, ay - cy)
  const d4 = crossProduct(dx - cx, dy - cy, bx - cx, by - cy)
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
}

export const segmentHitsRect = (x1: number, y1: number, x2: number, y2: number, rect: SelectionRect) => {
  if (pointInRect(x1, y1, rect) || pointInRect(x2, y2, rect)) return true
  const { left, top, right, bottom } = rect
  const rectEdges: [number, number, number, number][] = [
    [left, top, right, top],
    [right, top, right, bottom],
    [right, bottom, left, bottom],
    [left, bottom, left, top],
  ]
  return rectEdges.some(([ex1, ey1, ex2, ey2]) => segmentsCross(x1, y1, x2, y2, ex1, ey1, ex2, ey2))
}

export const isStraightLinePath = (pathData: string) => {
  return /^[\s\d.,eE+-]*[ML]/i.test(pathData) && !/[CQAHSVZ]/i.test(pathData)
}

export const pathHitsRect = (pathData: string, rect: SelectionRect) => {
  const commands = pathData.match(/[ML][^ML]*/gi)
  if (!commands) return false
  let currentX = 0
  let currentY = 0
  return commands.some(command => {
    const coordinates = command
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number)
    const isRelative = command[0] === command[0].toLowerCase()
    const isMove = command[0].toUpperCase() === 'M'
    let index = 0
    let hit = false
    while (index + 1 < coordinates.length) {
      const nextX = isRelative ? currentX + coordinates[index] : coordinates[index]
      const nextY = isRelative ? currentY + coordinates[index + 1] : coordinates[index + 1]
      if (!isMove || index > 0) hit = hit || segmentHitsRect(currentX, currentY, nextX, nextY, rect)
      currentX = nextX
      currentY = nextY
      index += 2
    }
    return hit
  })
}

export const boxOverlapsRect = (box: Box2D, rect: SelectionRect) => {
  return box.x < rect.right && box.x + box.width > rect.left && box.y < rect.bottom && box.y + box.height > rect.top
}

export const centerOf = (id: number, box: Box2D): CenterPoint => {
  return { id, x: box.x + box.width / 2, y: box.y + box.height / 2 }
}

export const findPositionsNearCage = (
  cage: CenterPoint,
  cageCenters: CenterPoint[],
  positionCenters: CenterPoint[],
) => {
  return positionCenters
    .filter(position => {
      let nearestDistance = Infinity
      let nearestCageId = -1
      cageCenters.forEach(candidate => {
        const distance = Math.hypot(candidate.x - position.x, candidate.y - position.y)
        if (distance < nearestDistance) {
          nearestDistance = distance
          nearestCageId = candidate.id
        }
      })
      const distanceToCage = Math.hypot(cage.x - position.x, cage.y - position.y)
      return nearestCageId === cage.id || distanceToCage <= nearestDistance * CAGE_PROXIMITY_TOLERANCE
    })
    .map(position => position.id)
}

export const computeZoomAnchor = (
  clientX: number,
  clientY: number,
  hostRect: DOMRect,
  viewportRect: DOMRect,
): ZoomAnchor => {
  return {
    ratioX: (clientX - hostRect.left) / hostRect.width,
    ratioY: (clientY - hostRect.top) / hostRect.height,
    offsetX: clientX - viewportRect.left,
    offsetY: clientY - viewportRect.top,
  }
}

export const computeWheelZoom = (currentZoom: number, deltaY: number, deltaMode: number, viewportHeight: number) => {
  let deltaPx = deltaY
  if (deltaMode === 1) deltaPx *= WHEEL_LINE_HEIGHT_PX
  else if (deltaMode === 2) deltaPx *= viewportHeight
  return clampZoom(currentZoom * Math.exp(-deltaPx * WHEEL_ZOOM_SENSITIVITY))
}

export const computeTooltipOffset = (
  clientX: number,
  clientY: number,
  containerRect: DOMRect,
  tooltipWidth: number,
  tooltipHeight: number,
) => {
  const cursorX = clientX - containerRect.left
  const cursorY = clientY - containerRect.top
  let offsetX = cursorX + TOOLTIP_CURSOR_GAP_X
  let offsetY = cursorY + TOOLTIP_CURSOR_GAP_Y
  if (offsetX + tooltipWidth > containerRect.width - TOOLTIP_EDGE_PADDING) {
    offsetX = cursorX - tooltipWidth - TOOLTIP_FLIP_GAP
  }
  if (offsetY + tooltipHeight > containerRect.height - TOOLTIP_EDGE_PADDING) {
    offsetY = cursorY - tooltipHeight - TOOLTIP_FLIP_GAP
  }
  return { x: Math.max(TOOLTIP_EDGE_PADDING, offsetX), y: Math.max(TOOLTIP_EDGE_PADDING, offsetY) }
}
